import React, { useState, useEffect, useRef } from 'react';
import { Mic, X, Check, Loader2, XCircle, Calendar, User, Folder, FileText } from 'lucide-react';
import { format, addDays, nextMonday, parse, isValid } from 'date-fns';
import { es } from 'date-fns/locale';
import { useTask } from '../contexts/TaskContext';
import { toast } from 'react-hot-toast';

const VoiceAssistant = ({ sections, users, onAddTask }) => {
    const [isListening, setIsListening] = useState(false);
    const [transcript, setTranscript] = useState('');
    const [isProcessing, setIsProcessing] = useState(false);
    const [parsedData, setParsedData] = useState(null);
    const recognitionRef = useRef(null);

    // Initialize Speech Recognition
    useEffect(() => {
        if ('webkitSpeechRecognition' in window || 'SpeechRecognition' in window) {
            const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
            recognitionRef.current = new SpeechRecognition();
            recognitionRef.current.continuous = false;
            recognitionRef.current.interimResults = true;
            recognitionRef.current.lang = 'es-ES';

            recognitionRef.current.onresult = (event) => {
                let currentTranscript = '';
                for (let i = event.resultIndex; i < event.results.length; ++i) {
                    currentTranscript += event.results[i][0].transcript;
                }
                setTranscript(currentTranscript);
            };

            recognitionRef.current.onend = () => {
                if (isListening) {
                    // If it stopped but we are still "listening" state (e.g. silence), we might want to process
                    // But usually onend means it's done.
                    handleStopListening();
                }
            };

            recognitionRef.current.onerror = (event) => {
                console.error('Speech recognition error', event.error);
                setIsListening(false);
                toast.error('Error al escuchar. Inténtalo de nuevo.');
            };
        } else {
            console.warn('Speech Recognition API not supported in this browser.');
        }
    }, []);

    const handleStartListening = () => {
        if (recognitionRef.current) {
            setTranscript('');
            setParsedData(null);
            setIsListening(true);
            recognitionRef.current.start();
        } else {
            toast.error('Tu navegador no soporta comandos de voz.');
        }
    };

    const handleStopListening = () => {
        setIsListening(false);
        if (recognitionRef.current) {
            recognitionRef.current.stop();
        }

        if (transcript.trim().length > 3) {
            processCommand(transcript);
        }
    };

    // --- "AI" Parsing Logic (Mock) ---
    const processCommand = (text) => {
        setIsProcessing(true);

        // 1. Detect Assignee
        let detectedUser = null;
        const lowerText = text.toLowerCase();

        // Simple improved matching: exact name or first name
        // Sort users by name length desc to match full names first
        const sortedUsers = [...users].sort((a, b) => b.displayName.length - a.displayName.length);

        for (const user of sortedUsers) {
            const nameParts = user.displayName.toLowerCase().split(' ');
            // Check full name or first name
            if (lowerText.includes(user.displayName.toLowerCase()) || lowerText.includes(`para ${nameParts[0]}`)) {
                detectedUser = user;
                break;
            }
        }

        // 2. Detect Date
        let detectedDate = null;
        if (lowerText.includes('mañana')) {
            detectedDate = addDays(new Date(), 1);
        } else if (lowerText.includes('lunes')) {
            detectedDate = nextMonday(new Date());
        } else if (lowerText.includes('hoy')) {
            detectedDate = new Date();
        }
        // Very basic parsing for "15 de febrero" etc could be added here with regex

        // 3. Detect Section (Context)
        let detectedSection = null;
        let targetSectionId = sections.length > 0 ? sections[0].id : null; // Default to first section

        for (const section of sections) {
            if (lowerText.includes(section.nombre.toLowerCase()) || lowerText.includes(section.titulo?.toLowerCase())) {
                detectedSection = section;
                targetSectionId = section.id;
                break;
            }
        }

        // 4. Extract Title (Remove detected keywords roughly)
        // This is hard to do perfectly without NLP, so we'll just use the full text for now
        // or try to strip "para [Persona]" if we found one.
        let cleanTitle = text;
        // Optional: Clean up title... 

        setTimeout(() => {
            setParsedData({
                title: cleanTitle.charAt(0).toUpperCase() + cleanTitle.slice(1),
                assignee: detectedUser,
                date: detectedDate,
                sectionId: targetSectionId,
                sectionName: detectedSection ? (detectedSection.nombre || detectedSection.titulo) : sections.find(s => s.id === targetSectionId)?.titulo || 'General'
            });
            setIsProcessing(false);
        }, 800); // Fake "thinking" delay
    };

    const handleConfirm = () => {
        if (!parsedData) return;

        onAddTask({
            titulo: parsedData.title,
            seccionId: parsedData.sectionId,
            asignados: parsedData.assignee ? [parsedData.assignee.email] : [],
            fechaVencimiento: parsedData.date ? format(parsedData.date, 'yyyy-MM-dd') : null,
            estado: 'sin-iniciar'
        });

        toast.success('Tarea creada con voz');
        setParsedData(null);
        setTranscript('');
    };

    const handleCancel = () => {
        setParsedData(null);
        setTranscript('');
    };


    // --- Render ---

    // Floating Button
    if (!isListening && !parsedData && !isProcessing) {
        return (
            <button
                onClick={handleStartListening}
                className="fixed bottom-8 right-8 w-14 h-14 bg-gradient-to-r from-indigo-500 to-purple-600 rounded-full shadow-lg hover:shadow-xl hover:scale-105 transition-all duration-300 flex items-center justify-center z-50 group"
            >
                <div className="absolute inset-0 rounded-full bg-white opacity-0 group-hover:opacity-20 transition-opacity"></div>
                <Mic className="text-white w-6 h-6" />
            </button>
        );
    }

    return (
        <div className="fixed inset-0 bg-black/40 backdrop-blur-sm z-50 flex items-center justify-center p-4">

            {/* Voice Listening UI */}
            {(isListening || isProcessing) && !parsedData && (
                <div className="bg-white rounded-2xl shadow-2xl p-8 max-w-md w-full flex flex-col items-center animate-in fade-in zoom-in duration-200">
                    <div className={`w-20 h-20 rounded-full flex items-center justify-center mb-6 transition-colors ${isListening ? 'bg-red-50 text-red-500 animate-pulse' : 'bg-indigo-50 text-indigo-500'
                        }`}>
                        {isProcessing ? <Loader2 className="w-8 h-8 animate-spin" /> : <Mic className="w-8 h-8" />}
                    </div>

                    <h3 className="text-xl font-semibold text-gray-800 mb-2">
                        {isProcessing ? 'Procesando...' : 'Escuchando...'}
                    </h3>

                    <p className="text-gray-500 text-center mb-8 min-h-[3rem]">
                        {transcript || "Di algo como: 'Crear tarea revisar planos para mañana asignado a Juan'"}
                    </p>

                    <button
                        onClick={handleStopListening}
                        className="flex items-center gap-2 px-6 py-2.5 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-full font-medium transition-colors"
                    >
                        <XCircle size={18} />
                        Detener
                    </button>
                </div>
            )}

            {/* Confirmation Card UI */}
            {parsedData && (
                <div className="bg-white rounded-2xl shadow-2xl overflow-hidden max-w-md w-full animate-in fade-in slide-in-from-bottom-4 duration-300 border border-gray-100/50">
                    <div className="bg-gradient-to-r from-indigo-500 to-purple-600 px-6 py-4 flex items-center justify-between">
                        <h3 className="text-white font-medium flex items-center gap-2">
                            <FileText size={18} className="text-white/80" />
                            Confirmar Tarea
                        </h3>
                        <div className="bg-white/20 px-2 py-0.5 rounded text-xs text-white font-mono">
                            AI DETECTED
                        </div>
                    </div>

                    <div className="p-6 space-y-4">
                        {/* Title Input (Editable) */}
                        <div>
                            <label className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-1 block">Tarea</label>
                            <input
                                value={parsedData.title}
                                onChange={(e) => setParsedData({ ...parsedData, title: e.target.value })}
                                className="w-full text-lg font-medium text-gray-800 border-b-2 border-transparent hover:border-gray-200 focus:border-indigo-500 focus:outline-none py-1 transition-colors"
                            />
                        </div>

                        <div className="grid grid-cols-2 gap-4">
                            <div className="bg-gray-50 p-3 rounded-xl border border-gray-100">
                                <div className="flex items-center gap-2 mb-1.5 text-gray-500">
                                    <User size={14} />
                                    <span className="text-xs font-medium">Asignado a</span>
                                </div>
                                <div className="flex items-center gap-2">
                                    {parsedData.assignee ? (
                                        <>
                                            {parsedData.assignee.photoURL ? (
                                                <img src={parsedData.assignee.photoURL} alt="" className="w-5 h-5 rounded-full" />
                                            ) : (
                                                <div className="w-5 h-5 rounded-full bg-indigo-100 text-indigo-600 flex items-center justify-center text-xs font-bold">
                                                    {parsedData.assignee.displayName.charAt(0)}
                                                </div>
                                            )}
                                            <span className="text-sm font-medium text-gray-800 truncate">{parsedData.assignee.displayName.split(' ')[0]}</span>
                                        </>
                                    ) : (
                                        <span className="text-sm text-gray-400 italic">Sin asignar</span>
                                    )}
                                </div>
                            </div>

                            <div className="bg-gray-50 p-3 rounded-xl border border-gray-100">
                                <div className="flex items-center gap-2 mb-1.5 text-gray-500">
                                    <Calendar size={14} />
                                    <span className="text-xs font-medium">Fecha</span>
                                </div>
                                <span className={`text-sm font-medium ${parsedData.date ? 'text-gray-800' : 'text-gray-400 italic'}`}>
                                    {parsedData.date ? format(parsedData.date, "d 'de' MMMM", { locale: es }) : 'Sin fecha'}
                                </span>
                            </div>
                        </div>

                        <div className="bg-gray-50 p-3 rounded-xl border border-gray-100 flex items-center gap-3">
                            <div className="bg-white p-1.5 rounded-lg shadow-sm text-gray-400">
                                <Folder size={16} />
                            </div>
                            <div className="flex flex-col">
                                <span className="text-xs font-medium text-gray-500">Ubicación</span>
                                <span className="text-sm font-semibold text-gray-700">{parsedData.sectionName}</span>
                            </div>
                        </div>

                    </div>

                    <div className="p-4 bg-gray-50 border-t border-gray-100 flex gap-3">
                        <button
                            onClick={handleCancel}
                            className="flex-1 py-2.5 text-gray-600 font-medium hover:bg-gray-200 rounded-xl transition-colors"
                        >
                            Cancelar
                        </button>
                        <button
                            onClick={handleConfirm}
                            className="flex-1 py-2.5 bg-gray-900 hover:bg-black text-white font-medium rounded-xl shadow-lg shadow-gray-200/50 flex items-center justify-center gap-2 transition-all transform hover:scale-[1.02] active:scale-[0.98]"
                        >
                            <Check size={18} />
                            Crear Tarea
                        </button>
                    </div>
                </div>
            )}
        </div>
    );
};

export default VoiceAssistant;
